import { FC, useState, useEffect } from "react"
import { useRouter } from "next/router"
import styled from "styled-components"
import { InView } from "react-intersection-observer"
import type {
  ContentfulProductWithShopifyData,
  ProductContentSection,
} from "@lib/types"

import { useTracking, useShopifyProduct, useSite, useCustomer, useCheckout } from "@hooks/index"

import { ProductMedia, ProductRecommendations, CompleteTheLook } from "./index"
import { Container, DynamicComponent } from "@components/ui"
import ProductInfo from "./ProductInfo"
import { setChosenLocaleCookie } from "@lib/utils"

import {
  ProductReviews,
  ModalWithImage,
} from "@components/ui"
interface Props {
  page: ContentfulProductWithShopifyData
  reviewData: any
}

export const ProductPage: FC<Props> = ({ page, reviewData }) => {
  const {
    video,
    hideFromSite,
    hideFromCollections,
    contentSectionsCollection,
    completeTheLookCollection,
  } = page

  const site = useSite()
  const { track, tikTokTrack } = useTracking()

  const { shopifyProduct } = useShopifyProduct(page)

  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (hideFromSite) {
      router.push("/")
    }
    setModalOpen(!!hideFromCollections)
  }, [page])

  const router = useRouter()

  const { setCurrency } = useCustomer()
  const { setNewCurrencyCheckout } = useCheckout()
  const [media, setMedia] = useState<any>([])

   useEffect(() => {
    if (shopifyProduct?.id) {
      setMedia([...shopifyProduct?.images?.edges, ...(video ? [video] : [])])
    }
  }, [shopifyProduct])

  const handleBtnClick = () => {
    setChosenLocaleCookie("en-GB")
    setCurrency("GBP")
    setNewCurrencyCheckout("GB")
    router.push('/')
    setModalOpen(false)
  }

  return (
    <StyledProductPage>
      {site?.productNotAvailableModal  && (
        <ModalWithImage
          modalOpen={modalOpen}
          closeModal={() => setModalOpen(false)}
          handleBtnClick={handleBtnClick}
          richText={site.productNotAvailableModal?.richText}
          button={site.productNotAvailableModal?.button}
          image={site.productNotAvailableModal?.image}
          imageMobile={site.productNotAvailableModal?.imageMobile}
        />
      )}
      <Container>
        <ProductView>
          <div>
            <MediaWrapper>
              {media.length > 0 && <ProductMedia media={media} />}
            </MediaWrapper>
          </div>
          <div>
            <ProductInfo
              page={page}
              shopifyProduct={shopifyProduct}
              total_reviews={reviewData.total || 0}
            />
          </div>
        </ProductView>
      </Container>
      {contentSectionsCollection?.items.map(
        (section: ProductContentSection) => (
          <DynamicComponent key={section.sys.id} entry={section} />
        )
      )}
      {completeTheLookCollection.length > 0 && (
        <InView
          triggerOnce={true}
          onChange={inView => {
            const contentsForTikTok = completeTheLookCollection.map(product => ({
              content_id: product.slug,
              content_type: 'product',
              content_name: `${product.title}${product.color ? ` - ${product?.color?.name}` : ''}`,
              quantity: 1,
            }))
            if (inView) {
              track("Complete the Look Viewed", {
                name: shopifyProduct.title,
                handle: shopifyProduct?.handle,
                category: page?.category?.name,
              })
              tikTokTrack("ViewContent", {
                content_category: "Complete the Look Viewed",
                content_name: shopifyProduct.title,
                content_id: shopifyProduct?.handle,
                contents: contentsForTikTok
              })
            }
          }
          }
        >
          {({ ref }) => (
            <div ref={ref}>
              <CompleteTheLook products={completeTheLookCollection} />
            </div>
          )}
        </InView>
      )}
      {shopifyProduct?.id && (
        <InView
          triggerOnce={true}
          onChange={inView => {
            if (inView) {
              track("Related Products Viewed", {
                name: shopifyProduct.title,
                handle: shopifyProduct?.handle,
                category: page?.category?.name,
              })
              tikTokTrack("ViewContent", {
                content_category: "Related Products Viewed",
                content_name: shopifyProduct.title,
                content_id: shopifyProduct?.handle
              })
            }
          }
          }
        >
          {({ ref }) => (
            <div ref={ref}>
              <ProductRecommendations productId={shopifyProduct.id} />
            </div>
          )}
        </InView>
      )}
      {shopifyProduct?.id && reviewData.total > 0 && (
        <InView
          triggerOnce={true}
          onChange={inView => {
            if (inView) {
              track("Reviews Viewed", {
                name: shopifyProduct.title,
                handle: shopifyProduct?.handle,
                category: page?.category?.name,
              })
              tikTokTrack("ViewContent", {
                content_category: "Reviews Viewed",
                content_name: shopifyProduct.title,
                content_id: shopifyProduct?.handle
              })
            }
          }
          }
        >
          {({ ref }) => (
            <div ref={ref}>
              <ProductReviews
                reviews={reviewData.data || []}
                total_reviews={reviewData.total || 0}
              />
            </div>
          )}
        </InView>
      )}
    </StyledProductPage>
  )
}

const StyledProductPage = styled.div``

const MediaWrapper = styled.div`
  height: 149.86vw;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    position: sticky;
    top: ${({ theme }) => theme.navbar.md.height + 2}rem;
    height: auto;
  }
`

const ProductView = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    display: grid;
    grid-template-row: 1fr 1fr;
    grid-gap: 4.7rem;
    padding: 2rem;
    height: auto;
  }
  @media (min-width: ${({ theme }) => theme.breakpoint.lg}px) {
    grid-template-columns: 2fr 1fr;
    padding: 2.6rem 8.5rem 9.3rem 8.8rem;
  }
`
